import {
  Icons,
  Label,
  Sizes,
  TextElementTypes,
} from '@sede-enterprise/shell-ds-react-framework';
import ChartTooltip from 'components/Charting/Tooltip/ChartTooltip';
import {
  ChartTooltipHeader,
  SunIconWrapper,
} from 'components/Charting/Tooltip/ChartTooltip.styled';
import { DynatraceTriggerAction } from 'components/DynatraceTriggerAction/dynatrace-trigger-action';
import {
  TooltipColorBar,
  TooltipContainer,
  TooltipItem,
  TooltipItemText,
} from 'components/Tooltip/MarketAreaTradeDetail.styled';
import { DYNATRACE_ACTIONS } from 'constants/dynatrace-actions';
import { format } from 'date-fns';
import { useTooltipPosition } from 'hooks/useTooltipPosition/useTooltipPosition';
import {
  ContractedVolumeTooltipProps,
  MarketKey,
} from 'pages/Optimisation/components/ContractedVolume/types';
import {
  capitalizeSpecificFirstMarketWords,
  convertTitleCase,
} from 'pages/Optimisation/components/SummaryCards/RevenueBreakdownCard/utils';
import { isUtilisationMarket } from 'utils/optimisation';

const { SunAltSolid } = Icons;

const formatTime = (time: Date) => {
  return format(time, 'h:mmaaa OOO')
    .replace(':00', '')
    .replace('GMT+0', 'GMT')
    .replace('GMT+1', 'BST');
};

const ContractedVolumeTooltip = ({
  data,
  contractedPeriodTitle,
  color,
  isChangeOverDay,
  changeOverType,
}: ContractedVolumeTooltipProps) => {
  const { tooltipRef, tooltipStyle } = useTooltipPosition<HTMLDivElement>(
    165,
    10,
  );

  const priceUnit =
    data?.key === MarketKey.Ancillary && !isUtilisationMarket(data?.market)
      ? '£/MW/h'
      : '£/MWh';
  return (
    <ChartTooltip
      data-testid="lj-chart-tooltip"
      ref={tooltipRef}
      style={{
        ...tooltipStyle,
        zIndex: 101,
      }}
    >
      <ChartTooltipHeader>
        {isChangeOverDay ? (
          <Label
            type={TextElementTypes.Label}
            size={Sizes.Small}
            style={{ marginBottom: 6, display: 'flex', alignItems: 'center' }}
          >
            <SunIconWrapper>
              <SunAltSolid />
            </SunIconWrapper>
            {changeOverType} ChangeOver
          </Label>
        ) : null}
        <div>
          {format(data?.base?.start ?? data.start, 'd MMM y')},{' '}
          {formatTime(data?.base?.start ?? data.start)}
          {' - '}
          {formatTime(data?.base?.end ?? data.end)}
          {contractedPeriodTitle ? ', ' + contractedPeriodTitle : ''}
        </div>
      </ChartTooltipHeader>

      <TooltipContainer>
        <DynatraceTriggerAction
          action={DYNATRACE_ACTIONS.TOOLTIP_VOLUME_OVERVIEW}
        />
        <TooltipColorBar $color={color || 'white'} />
        <TooltipItem>
          {data.market && (
            <TooltipItemText>
              {capitalizeSpecificFirstMarketWords(
                convertTitleCase(
                  data?.market === 'longTermHedge' ? 'otc' : data?.market,
                ),
              )}{' '}
              {data.key === MarketKey.Wholesale && convertTitleCase(data?.side)}
            </TooltipItemText>
          )}
          <TooltipItemText>{data?.value ?? 0} MW</TooltipItemText>
          {data?.price != null && (
            <TooltipItemText>
              {data.price} {priceUnit}
            </TooltipItemText>
          )}
        </TooltipItem>
      </TooltipContainer>
    </ChartTooltip>
  );
};

export default ContractedVolumeTooltip;
